import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@features/ui';
import { cn } from '@lib/utils.ts';
import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { Input } from './input';

interface AdvancedPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
  pageSizeOptions?: number[];
}

export default function AdvancedPagination({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  className,
  pageSizeOptions = [10, 20, 50, 100],
}: AdvancedPaginationProps) {
  const [jumpToPage, setJumpToPage] = useState('');

  // Calculate the range of items currently displayed
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handleJumpToPage = () => {
    const pageNumber = parseInt(jumpToPage, 10);

    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setJumpToPage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  };

  // Update page size and reset to first page
  const handlePageSizeChange = (newSize: string) => {
    onPageSizeChange(parseInt(newSize, 10));
  };

  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row items-center justify-between gap-4 text-sm',
        className
      )}
    >
      {/* Left section: Items per page selector */}
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground whitespace-nowrap">
          Rows per page:
        </span>
        <Select
          value={pageSize.toString()}
          onValueChange={handlePageSizeChange}
        >
          <SelectTrigger className="h-9 w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Center section: Item range display */}
      <div className="text-muted-foreground">
        Showing {startItem}-{endItem} of {totalItems}
      </div>

      {/* Right section: Navigation controls */}
      <div className="flex items-center gap-2">
        {/* Go to page input */}
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground whitespace-nowrap">
            Go to:
          </span>
          <Input
            className="h-9 w-[70px]"
            inputSize="sm"
            max={totalPages}
            min={1}
            placeholder="Page"
            type="number"
            value={jumpToPage}
            onChange={(e) => setJumpToPage(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button
            className="h-9"
            disabled={
              !jumpToPage ||
              parseInt(jumpToPage, 10) < 1 ||
              parseInt(jumpToPage, 10) > totalPages
            }
            size="sm"
            variant="outline"
            onClick={handleJumpToPage}
          >
            Go
          </Button>
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <Button
            className="h-9 w-9 p-0"
            disabled={currentPage === 1}
            size="sm"
            variant="ghost"
            onClick={() => onPageChange(1)}
          >
            <ChevronsLeft size={16} />
          </Button>

          {/* Previous page */}
          <Button
            className="h-9 w-9 p-0"
            disabled={currentPage === 1}
            size="sm"
            variant="ghost"
            onClick={() => onPageChange(currentPage - 1)}
          >
            <ChevronLeft size={16} />
          </Button>

          {/* Page indicator */}
          <span className="mx-2 text-sm font-medium whitespace-nowrap">
            Page {currentPage} of {totalPages}
          </span>

          {/* Next page */}
          <Button
            className="h-9 w-9 p-0"
            disabled={currentPage === totalPages}
            size="sm"
            variant="ghost"
            onClick={() => onPageChange(currentPage + 1)}
          >
            <ChevronRight size={16} />
          </Button>

          {/* Last page */}
          <Button
            className="h-9 w-9 p-0"
            disabled={currentPage === totalPages}
            size="sm"
            variant="ghost"
            onClick={() => onPageChange(totalPages)}
          >
            <ChevronsRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export { AdvancedPagination };
